import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { LogoMark } from '@/components/generals/logo'
import { BOOK } from '@/constants/book'
import { siteConfig } from '@/constants/site'

type CoverSize = 'sm' | 'md' | 'lg'

interface BookCoverProps {
    className?: string
    size?: CoverSize
}

const SIZES: Record<CoverSize, { root: string, padding: string, brand: string, title: string, subtitle: string, mark: string }> = {
    sm: {
        root: 'w-24',
        padding: 'p-2.5 pl-4',
        brand: 'text-[0.4rem]',
        title: 'text-[0.8rem]',
        subtitle: 'hidden',
        mark: 'size-4'
    },
    md: {
        root: 'w-full max-w-[11rem]',
        padding: 'p-4 pl-6',
        brand: 'text-[0.5rem]',
        title: 'text-xl',
        subtitle: 'text-[0.6rem]',
        mark: 'size-5'
    },
    lg: {
        root: 'w-full max-w-[18rem]',
        padding: 'p-7 pl-9',
        brand: 'text-[0.65rem]',
        title: 'text-[2.1rem]',
        subtitle: 'text-sm',
        mark: 'size-8'
    }
}

const BookCover: FC<BookCoverProps> = ({ className, size = 'lg' }): ReactNode => {
    const styles = SIZES[size]

    return (
        <div
            role='img'
            aria-label={`Cover of ${BOOK.title}`}
            className={cn(
                'relative aspect-[3/4] shrink-0 overflow-hidden rounded-r-xl rounded-l-sm bg-primary text-primary-foreground shadow-[0_24px_48px_-24px_oklch(0.3_0.05_165/0.55)]',
                styles.root,
                className
            )}
        >
            <div aria-hidden='true' className={cn('absolute inset-y-0 left-0 bg-black/15', size === 'sm' ? 'w-1.5' : 'w-3')} />

            <div
                aria-hidden='true'
                className='absolute inset-x-0 bottom-0 h-1/2 opacity-[0.14]'
                style={{ backgroundImage: 'repeating-linear-gradient(0deg, currentColor 0 1px, transparent 1px 18px)' }}
            />

            <div className={cn('relative flex h-full flex-col justify-between', styles.padding)}>
                <p className={cn('font-semibold tracking-[0.2em] uppercase text-primary-foreground/70', styles.brand)}>
                    {siteConfig.name}
                </p>

                <div>
                    <p className={cn('font-heading leading-[1.05] font-medium', styles.title)}>
                        {BOOK.title}
                    </p>

                    <p className={cn('mt-3 leading-snug text-primary-foreground/75', styles.subtitle)}>
                        {BOOK.subtitle}
                    </p>
                </div>

                <LogoMark className={cn('[&_rect:first-child]:fill-primary-foreground/15', styles.mark)} />
            </div>
        </div>
    )
}

export default BookCover
